import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ShoppingBag,
  MapPin,
  CreditCard,
  Calendar,
  PackageCheck,
  AlertCircle,
  XCircle,
  AlertTriangle,
  Copy,
  Check,
  HelpCircle,
  ExternalLink,
  Mail,
  Phone,
  X,
  MessageCircle,
  Truck,
  RotateCcw,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import * as ordersApi from "../api/ordersApi";
import {
  formatDate,
  formatMoney,
  formatOrderStatus,
  formatProductSize,
  formatGovernorate,
  formatShipmentStatus,
  formatDeliveryType,
} from "../utils/format";
import { resolveImageUrl } from "../utils/images";
import { getProductDetailUrl } from "../utils/productSlug";
import "./Orders.css";
import "./AccountForms.css";
import "./OrderDetail.css";

// Official WhatsApp icon SVG for concierge communication
function WhatsAppIcon({ size = 18, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { userId } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const { addToast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(false);
  const [copiedTrackingId, setCopiedTrackingId] = useState(null);

  const handleCopyOrderId = () => {
    if (!order?.id) return;
    const textToCopy = String(order.id);

    const onCopySuccess = () => {
      setCopiedOrderId(true);
      addToast(t("orderDetail.copiedToast"), "success");
      setTimeout(() => setCopiedOrderId(false), 2200);
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).then(onCopySuccess).catch(() => {
        fallbackCopy(textToCopy, onCopySuccess);
      });
    } else {
      fallbackCopy(textToCopy, onCopySuccess);
    }
  };

  const fallbackCopy = (text, onSuccess) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      onSuccess && onSuccess();
    } catch (err) {
      console.error("Failed to copy order ID:", err);
    }
  };

  const handleCopyTrackingNumber = (trackingNumber) => {
    if (!trackingNumber) return;
    const textToCopy = String(trackingNumber);

    const onCopySuccess = () => {
      setCopiedTrackingId(textToCopy);
      addToast(t("orderDetail.bostaCopiedTracking"), "success");
      setTimeout(() => setCopiedTrackingId(null), 2200);
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).then(onCopySuccess).catch(() => {
        fallbackCopy(textToCopy, onCopySuccess);
      });
    } else {
      fallbackCopy(textToCopy, onCopySuccess);
    }
  };

  useEffect(() => {
    if (!userId || !id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await ordersApi.getOrder(userId, id);
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) addToast(t("orderDetail.notFound"), "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, id, addToast, t]);

  useEffect(() => {
    if (!showCancelModal && !showSupportModal) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showSupportModal) setShowSupportModal(false);
        if (showCancelModal && !cancelling) setShowCancelModal(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showCancelModal, showSupportModal, cancelling]);

  const handleCancelOrder = async () => {
    if (!userId || !id || cancelling) return;
    setCancelling(true);
    try {
      const updatedOrder = await ordersApi.cancelUserOrder(userId, id);
      setOrder(updatedOrder || { ...order, orderStatus: "Cancelled" });
      addToast(t("orderDetail.cancelSuccess"), "success");
      setShowCancelModal(false);
    } catch (err) {
      addToast(err?.message || t("orderDetail.cancelError"), "error");
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const s = String(status).toLowerCase();
    if (s.includes("confirm") || s === "2") return "orders-badge--confirmed";
    if (s.includes("ship") || s === "4") return "orders-badge--shipping";
    if (s.includes("deliver") || s.includes("complet") || s === "5") return "orders-badge--delivered";
    if (s.includes("cancel") || s === "6") return "orders-badge--cancelled";
    return "orders-badge--pending";
  };

  const getDeliveryTypeMeta = (type) => {
    const tStr = String(type).toLowerCase();
    if (tStr.includes("return") || type === 25) {
      return {
        icon: RotateCcw,
        badgeClass: "bosta-type-badge--return",
      };
    }
    if (tStr.includes("exchange") || type === 30) {
      return {
        icon: RefreshCw,
        badgeClass: "bosta-type-badge--exchange",
      };
    }
    return {
      icon: Truck,
      badgeClass: "bosta-type-badge--forward",
    };
  };

  const getShipmentBadgeClass = (status) => {
    const s = String(status).toLowerCase();
    if (s.includes("deliver") || s === "6") return "bosta-badge--delivered";
    if (s.includes("transit") || s.includes("route") || s.includes("pickup") || s === "2" || s === "3" || s === "4" || s === "5") return "bosta-badge--in-transit";
    if (s.includes("return") || s === "7") return "bosta-badge--return";
    if (s.includes("cancel") || s.includes("terminat") || s === "9" || s === "10") return "bosta-badge--cancelled";
    return "bosta-badge--created";
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="orders-page">
        <div className="container" style={{ maxWidth: 720, textAlign: "center", padding: "60px 20px" }}>
          <AlertCircle size={44} style={{ color: "#dc2626", margin: "0 auto 16px" }} />
          <h2 style={{ fontFamily: "var(--font-display)", marginBottom: 8 }}>{t("orderDetail.notFound")}</h2>
          <Link to="/account/orders" className="btn-outline" style={{ marginTop: 16 }}>
            {t("orderDetail.backToOrders")}
          </Link>
        </div>
      </div>
    );
  }

  const ship = order.shippingAddress;
  const bill = order.billingAddress;
  const statusLabel = formatOrderStatus(order.orderStatus, language);
  const statusStr = String(order.orderStatus).toLowerCase();
  const canCancel =
    order.orderStatus === "Pending" ||
    order.orderStatus === "Confirmed" ||
    order.orderStatus === 1 ||
    order.orderStatus === 2 ||
    statusStr === "pending" ||
    statusStr === "confirmed";

  const isShippingOrHigher =
    order.orderStatus === "Shipping" ||
    order.orderStatus === "Completed" ||
    order.orderStatus === "Returned" ||
    order.orderStatus === 4 ||
    order.orderStatus === 5 ||
    order.orderStatus === 7 ||
    statusStr.includes("ship") ||
    statusStr.includes("complet") ||
    statusStr.includes("deliver") ||
    statusStr.includes("return");

  const shipments = (order?.shipments || [])
    .filter((s) => s.trackingNumber || s.status)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const showTrackingSection = isShippingOrHigher || shipments.length > 0;

  const shortOrderId = order?.id ? String(order.id).slice(0, 8) : "";
  const bostaTrackingPortalUrl = isRTL
    ? "https://bosta.co/ar-eg/tracking-shipments"
    : "https://bosta.co/en-eg/tracking-shipments";
  const whatsappMessage = isRTL
    ? `مرحباً تيري لاند، أحتاج مساعدة أو استفسار/طلب إرجاع بخصوص طلبي #${shortOrderId} (معرف الطلب: ${order?.id})`
    : `Hello Terry Land, I have an inquiry or wish to request assistance/return for Order #${shortOrderId} (Order ID: ${order?.id})`;
  const whatsappUrl = `https://wa.me/201099921862?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="orders-page">
      <div className="container order-detail">
        <Link to="/account/orders" className="form-back btn-ghost">
          <ArrowLeft size={15} className="rtl-flip" />
          <span>{t("orderDetail.back")}</span>
        </Link>

        {/* Order Header */}
        <div className="order-detail__head">
          <div className="order-detail__head-info">
            <span className="section-label">{t("orderDetail.sectionLabel")}</span>
            <h1 className="orders-page__title" style={{ marginBottom: 8 }}>
              <span>{t("orderDetail.orderNumber")}</span>{" "}
              <span
                className="order-title-code"
                onClick={handleCopyOrderId}
                title={copiedOrderId ? t("orderDetail.copied") : t("orderDetail.copyOrderNumber")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCopyOrderId();
                  }
                }}
              >
                #{String(order.id).slice(0, 8)}
              </span>
            </h1>
            <div className="order-detail__meta-row">
              <button
                type="button"
                className={`order-id-copy-action ${copiedOrderId ? "order-id-copy-action--copied" : ""}`}
                onClick={handleCopyOrderId}
                title={copiedOrderId ? t("orderDetail.copied") : t("orderDetail.copyOrderNumber")}
                aria-label={copiedOrderId ? t("orderDetail.copied") : t("orderDetail.copyOrderNumber")}
              >
                {copiedOrderId ? <Check size={12} strokeWidth={2.5} /> : <Copy size={12} />}
                <span>{copiedOrderId ? t("orderDetail.copied") : t("orderDetail.copyOrderNumber")}</span>
              </button>
              <span className="order-detail__date">
                <Calendar size={13} />
                <span>{formatDate(order.createdAt, language)}</span>
              </span>
              <span className={`orders-badge ${getStatusBadgeClass(order.orderStatus)}`}>
                {statusLabel}
              </span>
            </div>
          </div>

          <div className="order-detail__actions-wrap">
            <div className="order-detail__total">
              <span>{t("orderDetail.total")}</span>
              <strong>{formatMoney(order.totalPrice, language)}</strong>
            </div>
            <div className="order-detail__btns-group">
              <button
                type="button"
                className="btn-order-support"
                onClick={() => setShowSupportModal(true)}
                title={t("orderDetail.supportBtnTooltip")}
              >
                <HelpCircle size={14} />
                <span>{t("orderDetail.supportBtn")}</span>
              </button>
              {canCancel && (
                <button
                  type="button"
                  className="btn-cancel-order"
                  onClick={() => setShowCancelModal(true)}
                >
                  <XCircle size={14} />
                  <span>{t("orderDetail.cancelOrder")}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showCancelModal && (
          <div
            className="cancel-modal-overlay"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget && !cancelling) {
                setShowCancelModal(false);
              }
            }}
          >
            <div
              className="cancel-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancel-modal-title"
              aria-describedby="cancel-modal-desc"
            >
              <div className="cancel-modal__icon-wrap">
                <AlertTriangle size={26} className="cancel-modal__icon" />
              </div>
              <h3 id="cancel-modal-title" className="cancel-modal__title">
                {t("orderDetail.cancelConfirmTitle")}
              </h3>
              <p id="cancel-modal-desc" className="cancel-modal__desc">
                {t("orderDetail.cancelConfirmMsg")}
              </p>
              <div className="cancel-modal__actions">
                <button
                  type="button"
                  className="btn-cancel-confirm"
                  onClick={handleCancelOrder}
                  disabled={cancelling}
                >
                  {cancelling ? (
                    <>
                      <span className="btn-spinner" aria-hidden="true" />
                      <span>{t("orderDetail.cancelling")}</span>
                    </>
                  ) : (
                    <>
                      <XCircle size={15} />
                      <span>{t("orderDetail.cancelConfirmBtn")}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn-cancel-dismiss"
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelling}
                >
                  {t("orderDetail.cancelKeepBtn")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Support & Returns Inquiry Concierge Modal */}
        {showSupportModal && (
          <div
            className="cancel-modal-overlay"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowSupportModal(false);
              }
            }}
          >
            <div
              className="order-support-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="support-modal-title"
            >
              <button
                type="button"
                className="order-support-modal__close"
                onClick={() => setShowSupportModal(false)}
                aria-label={t("orderDetail.closeModal")}
              >
                <X size={18} />
              </button>

              <div className="order-support-modal__header">
                <div className="order-support-modal__icon-wrap">
                  <HelpCircle size={26} />
                </div>
                <h3 id="support-modal-title" className="order-support-modal__title">
                  {t("orderDetail.supportModalTitle")}
                </h3>
                <p className="order-support-modal__subtitle">
                  {t("orderDetail.supportModalSubtitle")}
                </p>
              </div>

              <div className="order-support-modal__options">
                {/* WhatsApp Direct Option */}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="order-support-card order-support-card--whatsapp"
                >
                  <div className="order-support-card__icon order-support-card__icon--whatsapp">
                    <WhatsAppIcon size={22} />
                  </div>
                  <div className="order-support-card__body">
                    <strong className="order-support-card__title">
                      {t("orderDetail.supportWhatsappTitle")}
                    </strong>
                    <span className="order-support-card__desc">
                      {t("orderDetail.supportWhatsappDesc")}
                    </span>
                  </div>
                  <ExternalLink size={16} className="order-support-card__arrow rtl-flip" />
                </a>

                {/* Contact Us Form Option */}
                <Link
                  to="/contact"
                  state={{ orderId: order.id, orderNumber: shortOrderId }}
                  className="order-support-card order-support-card--contact"
                  onClick={() => setShowSupportModal(false)}
                >
                  <div className="order-support-card__icon order-support-card__icon--gold">
                    <Mail size={20} />
                  </div>
                  <div className="order-support-card__body">
                    <strong className="order-support-card__title">
                      {t("orderDetail.supportContactTitle")}
                    </strong>
                    <span className="order-support-card__desc">
                      {t("orderDetail.supportContactDesc")}
                    </span>
                  </div>
                  <ExternalLink size={16} className="order-support-card__arrow rtl-flip" />
                </Link>

                {/* Direct Telephone Option */}
                <a
                  href="tel:+201099921862"
                  className="order-support-card order-support-card--phone"
                >
                  <div className="order-support-card__icon order-support-card__icon--taupe">
                    <Phone size={19} />
                  </div>
                  <div className="order-support-card__body">
                    <strong className="order-support-card__title">
                      {t("orderDetail.supportCallTitle")}
                    </strong>
                    <span className="order-support-card__desc" dir="ltr">
                      +20 10 9992 1862 • {t("orderDetail.supportCallDesc")}
                    </span>
                  </div>
                  <ExternalLink size={16} className="order-support-card__arrow rtl-flip" />
                </a>
              </div>

              <div className="order-support-modal__footer">
                <button
                  type="button"
                  className="btn-outline"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => setShowSupportModal(false)}
                >
                  {t("orderDetail.closeModal")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bosta Shipment Tracking Section (Active for Shipping and higher states or whenever shipments exist) */}
        {showTrackingSection && (
          <section className="order-bosta-tracking">
            <div className="order-bosta-tracking__header">
              <div className="order-bosta-tracking__title-wrap">
                <div className="order-bosta-tracking__icon-badge">
                  <Truck size={18} />
                </div>
                <div>
                  <h3 className="order-bosta-tracking__title">
                    {t("orderDetail.bostaTrackingTitle")}
                  </h3>
                  <p className="order-bosta-tracking__subtitle">
                    {t("orderDetail.bostaTrackingSubtitle")}
                  </p>
                </div>
              </div>
            </div>

            {shipments.length === 0 ? (
              <div className="order-bosta-tracking__empty">
                <p>{t("orderDetail.bostaPreparing")}</p>
              </div>
            ) : (
              <div className="order-bosta-tracking__list">
                {shipments.length > 1 && (
                  <div className="order-bosta-tracking__multiple-notice">
                    <AlertCircle size={15} />
                    <span>{t("orderDetail.bostaMultipleNotice")}</span>
                  </div>
                )}

                {shipments.map((shipment) => {
                  const typeMeta = getDeliveryTypeMeta(shipment.deliveryType);
                  const TypeIcon = typeMeta.icon;
                  const typeLabel = formatDeliveryType(shipment.deliveryType, language);
                  const statusLabelText = formatShipmentStatus(shipment.status, language);
                  const trackingNum = shipment.trackingNumber;
                  const isCopied = copiedTrackingId === String(trackingNum);

                  return (
                    <div key={shipment.id || trackingNum} className="order-bosta-card">
                      <div className="order-bosta-card__main">
                        <div className="order-bosta-card__type-row">
                          <span className={`order-bosta-card__type-badge ${typeMeta.badgeClass}`}>
                            <TypeIcon size={13} />
                            <span>{typeLabel}</span>
                          </span>

                          <span className={`order-bosta-card__status-badge ${getShipmentBadgeClass(shipment.status)}`}>
                            {statusLabelText}
                          </span>

                          {shipment.createdAt && (
                            <span className="order-bosta-card__date">
                              <Calendar size={12} />
                              <span>{formatDate(shipment.createdAt, language)}</span>
                            </span>
                          )}
                        </div>

                        {trackingNum ? (
                          <div className="order-bosta-card__track-row">
                            <span className="order-bosta-card__label">
                              {t("orderDetail.bostaTrackingNumber")}:
                            </span>
                            <div className="order-bosta-card__code-wrap">
                              <span
                                className="order-bosta-card__code"
                                onClick={() => handleCopyTrackingNumber(trackingNum)}
                                title={isCopied ? t("orderDetail.bostaCopiedTracking") : t("orderDetail.bostaCopyTracking")}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleCopyTrackingNumber(trackingNum);
                                  }
                                }}
                              >
                                {trackingNum}
                              </span>
                              <button
                                type="button"
                                className={`order-bosta-card__copy-btn ${isCopied ? "order-bosta-card__copy-btn--copied" : ""}`}
                                onClick={() => handleCopyTrackingNumber(trackingNum)}
                                title={isCopied ? t("orderDetail.bostaCopiedTracking") : t("orderDetail.bostaCopyTracking")}
                                aria-label={isCopied ? t("orderDetail.bostaCopiedTracking") : t("orderDetail.bostaCopyTracking")}
                              >
                                {isCopied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} />}
                                <span>{isCopied ? t("orderDetail.bostaCopiedTracking") : t("orderDetail.bostaCopyTracking")}</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="order-bosta-card__pending-note">
                            {t("orderDetail.bostaPreparing")}
                          </div>
                        )}
                      </div>

                      {trackingNum && (
                        <div className="order-bosta-card__actions">
                          <a
                            href={`${bostaTrackingPortalUrl}?shipment-number=${encodeURIComponent(trackingNum)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-order-bosta-track"
                          >
                            <span>{t("orderDetail.bostaTrackBtn")}</span>
                            <ExternalLink size={13} className="rtl-flip" />
                          </a>
                          {shipment.airwayBillUrl && (
                            <a
                              href={shipment.airwayBillUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-order-bosta-awb"
                            >
                              <span>{t("orderDetail.bostaAirwayBill")}</span>
                              <ExternalLink size={12} className="rtl-flip" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Addresses Grid */}
        <div className="order-detail__grid">
          {ship && (
            <div className="order-detail__card">
              <div className="order-detail__card-header">
                <MapPin size={16} className="order-detail__card-icon" />
                <h4>{t("orderDetail.shippingTitle")}</h4>
              </div>
              <p className="order-detail__address-primary">
                {formatGovernorate(ship.governorate, language)}, {ship.country}
              </p>
              {ship.description && <p>{ship.description}</p>}
              {ship.apartmentInfo && <p>{ship.apartmentInfo}</p>}
              {ship.phoneNumber && <p className="order-detail__phone">{ship.phoneNumber}</p>}
            </div>
          )}

          {bill && (
            <div className="order-detail__card">
              <div className="order-detail__card-header">
                <CreditCard size={16} className="order-detail__card-icon" />
                <h4>{t("orderDetail.billingTitle")}</h4>
              </div>
              <p className="order-detail__address-primary">
                {formatGovernorate(bill.governorate, language)}, {bill.country}
              </p>
              {bill.description && <p>{bill.description}</p>}
              {bill.phoneNumber && <p className="order-detail__phone">{bill.phoneNumber}</p>}
            </div>
          )}
        </div>

        {/* Order Items Table */}
        <h3 className="order-detail__items-title">{t("orderDetail.itemsTitle")}</h3>
        <ul className="order-items">
          {(order.orderItems || []).map((item) => {
            const sizeLabel = formatProductSize(item.productSize, language);
            return (
              <li key={item.id} className="order-item">
                <div
                  className="order-item__thumb"
                  style={{
                    background: item.colorImageUrl
                      ? `url(${resolveImageUrl(item.colorImageUrl)}) center/cover`
                      : "var(--surface-input)",
                  }}
                />
                <div className="order-item__info">
                  <strong>{item.productName}</strong>
                  <span className="order-item__sub">
                    {[sizeLabel, item.color].filter(Boolean).join(" · ")}
                  </span>
                </div>
                <span className="order-item__qty">×{item.quantity}</span>
                <span className="order-item__price">{formatMoney(item.price, language)}</span>
                {item.productId && (
                  <Link
                    to={getProductDetailUrl({ id: item.productId, name: item.productName })}
                    state={{ productId: item.productId }}
                    className="order-item__buy-btn"
                  >
                    <ShoppingBag size={14} />
                    <span>{t("orderDetail.buyAgain")}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        {/* Dedicated Bottom Assistance & Return Concierge Banner */}
        <section className="order-detail__support-banner">
          <div className="order-detail__support-banner-content">
            <div className="order-detail__support-banner-icon">
              <HelpCircle size={24} />
            </div>
            <div className="order-detail__support-banner-text">
              <h4>{t("orderDetail.supportBannerTitle")}</h4>
              <p>{t("orderDetail.supportBannerDesc")}</p>
            </div>
          </div>
          <div className="order-detail__support-banner-actions">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-order-support-whatsapp"
            >
              <WhatsAppIcon size={16} />
              <span>{t("orderDetail.whatsappAction")}</span>
            </a>
            <Link
              to="/contact"
              state={{ orderId: order.id, orderNumber: shortOrderId }}
              className="btn-outline btn-order-support-contact"
            >
              <Mail size={15} />
              <span>{t("orderDetail.contactAction")}</span>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
